"use client";

import Container from "@/components/Container";
import GradientLightText from "@/components/GradientLightText";
import Section from "@/components/Section";
import TestimonilaCard from "@/components/TestimonialCard";
import Text from "@/components/Text";
import TextBlock from "@/components/TextBlock";
import { transformText } from "@/utils/transformText";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperTestimonialsButtons from "@/components/SwiperTestimonialsButtons";
import { Autoplay } from "swiper/modules";
import { useState } from "react";
import { Swiper as SwiperCore } from "swiper";
import dynamic from "next/dynamic";

const DynamicVideoTestimonialsWithNumber = dynamic(
  () => import("@/components/VideoTestimonialsWithNumbers")
);
const DynamicCardTestimonialsWithNumbers = dynamic(
  () => import("@/components/CardTestimonialsWithNumbers")
);

const COMPONENTS = {
  VideoTestimonialsWithNumbers: DynamicVideoTestimonialsWithNumber,
  CardTestimonialsWithNumbers: DynamicCardTestimonialsWithNumbers,
};

type ComponentType = {
  type: string;
  content: Object;
};

export type TestimonialType = {
  image: {
    src: {
      desktop: string;
      tablet: string;
      mobile: string;
    };
    alt: string;
  };
  text: string;
  name: string;
};

type ContenType = {
  isLight?: boolean;
  subtitleTag?: string;
  title: string;
  subtitle?: string;
  body?: string;
  bodyTwo?: string;
  testimonials: TestimonialType[];
};

type TestimonialsProps = {
  section: {
    type: string;
    content: ContenType;
    components?: ComponentType[];
  };
};

const Testimonials = ({ section }: TestimonialsProps) => {
  const [isNavigationVisible, setIsNavigationVisible] = useState(true);

  const checkSlidesCount = (swiper: SwiperCore) => {
    const slidesPerView = swiper.params.slidesPerView;
    const enoughSlides = swiper.slides.length > Number(slidesPerView);
    setIsNavigationVisible(enoughSlides);
  };
  return (
    <Section
      isPaddingTop={section.content.isLight ? false : true}
      isPaddingBottom={section.content.isLight ? false : true}
      isSmallTopPadding={section.content.isLight ? true : false}
      isSmallBottomPadding={section.content.isLight ? true : false}
      className={`testimonials ${
        section.content.isLight ? "bg-white darker-gold" : ""
      }`.trim()}
    >
      <Container>
        <TextBlock className="text-block--center text-block--wider">
          {section.content.subtitleTag ? (
            <Text
              element="span"
              elementStyle="subtitle-secondary"
              className="weight-700"
            >
              {section.content.subtitleTag}
            </Text>
          ) : null}
          <Text element="h2" elementStyle="h1">
            {transformText(section.content.title, {
              gradient: (chunk) => (
                <GradientLightText
                  element="span"
                  classNames="light-gradient--no-shadow"
                >
                  {chunk}
                </GradientLightText>
              ),
              br: (chunk) => <br />,
            })}
          </Text>
          {!!section.content.subtitle ? (
            <Text element="p" elementStyle="subtitle" className="weight-700">
              {section.content.subtitle}
            </Text>
          ) : null}
          {!!section.content.body ? (
            <Text
              element="p"
              elementStyle="body"
              className="text-block--narrow center"
            >
              {section.content.body}
            </Text>
          ) : null}
          {!!section.content.bodyTwo ? (
            <Text element="p" elementStyle="body" className="weight-600">
              {section.content.bodyTwo}
            </Text>
          ) : null}
        </TextBlock>
        <Swiper
          className="testimonials-swiper"
          slidesPerView={1}
          spaceBetween={30}
          grabCursor={true}
          onInit={(swiper) => checkSlidesCount(swiper)}
          onResize={(swiper) => checkSlidesCount(swiper)}
          loop
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
          }}
          modules={[Autoplay]}
          breakpoints={{
            1200: {
              slidesPerView: 3,
              spaceBetween: 130,
            },
            768: {
              slidesPerView: 2,
              spaceBetween: 50,
            },
          }}
        >
          {section.content.testimonials.map((testimonial, index) => (
            <SwiperSlide key={testimonial.name + testimonial.text + index}>
              <TestimonilaCard
                content={testimonial}
                hideShadow={section.content.isLight}
              />
            </SwiperSlide>
          ))}
          {isNavigationVisible ? <SwiperTestimonialsButtons /> : null}
        </Swiper>
        {section.components?.map((component: ComponentType, index: number) => {
          const Component =
            COMPONENTS[component.type as keyof typeof COMPONENTS];
          if (!Component) return null;

          return <Component key={index} component={component as any} />;
        })}
      </Container>
    </Section>
  );
};

export default Testimonials;
